import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import FadeUp from '../components/FadeUp';
import SEO from '../components/SEO';

export default function Terms() {
  const { lang, t } = useLanguage();

  const seoDesc = lang === 'es'
    ? 'Términos de servicio de Anyelo Elite. Condiciones de uso, pagos, suscripciones y políticas de cancelación.'
    : 'Anyelo Elite terms of service. Usage conditions, payments, subscriptions and cancellation policies.';

  return (
    <main data-testid="terms-page" style={{ backgroundColor: '#030303', minHeight: '100vh' }}>
      <SEO
        title={lang === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
        description={seoDesc}
        path="/terms"
      />
      <div className="mx-auto max-w-[800px] px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <FadeUp>
          <h1
            className="text-3xl sm:text-4xl font-bold text-white mb-2"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}
          >
            {t('terms_title')}
          </h1>
          <p className="text-white/40 text-sm mb-10" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {lang === 'es' ? 'Última actualización: Marzo 2026' : 'Last updated: March 2026'}
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="prose-legal space-y-6 text-white/60 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {lang === 'es' ? <TermsContentES /> : <TermsContentEN />}
          </div>
        </FadeUp>
      </div>
    </main>
  );
}

function TermsContentES() {
  return (
    <>
      <p>
        Bienvenido a Anyelo Elite. Al usar esta aplicación, acepta estos términos.
      </p>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Descripción del servicio</h2>
        <p className="mb-3">Anyelo Elite es una aplicación de coaching de fitness personal que ofrece:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Reserva y gestión de sesiones de entrenamiento</li>
          <li>Videos de técnica de ejercicios</li>
          <li>Programas de entrenamiento estructurados</li>
          <li>Planes de nutrición</li>
          <li>Chat de coaching 1-a-1</li>
          <li>Videoconsultas</li>
          <li>Seguimiento de progreso</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Elegibilidad</h2>
        <p>Debe tener al menos 16 años para usar esta aplicación. Al usar la aplicación, confirma que cumple con este requisito.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Cuentas de usuario</h2>
        <p>Usted es responsable de mantener la confidencialidad de las credenciales de su cuenta. Acepta proporcionar información precisa durante el registro.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Uso aceptable</h2>
        <p className="mb-3">Acepta no:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Compartir su cuenta con otros</li>
          <li>Usar la aplicación para cualquier propósito ilegal</li>
          <li>Intentar acceder a datos de otros usuarios</li>
          <li>Interferir con la funcionalidad de la aplicación</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pagos y suscripciones</h2>
        <p>Los pagos por sesiones de video consulta se procesan de forma segura a través de Stripe. Las suscripciones mensuales se renuevan automáticamente. Puede cancelar su suscripción en cualquier momento desde la configuración de su cuenta.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Descargo de responsabilidad de entrenamiento</h2>
        <p>Los programas de entrenamiento, planes de nutrición y consejos de coaching proporcionados a través de esta aplicación son solo con fines informativos. Siempre consulte a un profesional de la salud antes de comenzar cualquier nuevo programa de ejercicio o nutrición. Anyelo Elite no es responsable de las lesiones que puedan ocurrir durante el entrenamiento.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Propiedad intelectual</h2>
        <p>Todo el contenido de la aplicación, incluyendo videos, programas y diseños, es propiedad de Anyelo Elite y no puede ser reproducido sin permiso.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Videoconsultas</h2>
        <p>Las videoconsultas se facilitan a través de servicios de terceros (Jitsi Meet). No somos responsables de la disponibilidad o calidad de estos servicios de terceros.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Modificaciones</h2>
        <p>Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado de la aplicación constituye la aceptación de los términos modificados.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Limitación de responsabilidad</h2>
        <p>Anyelo Elite se proporciona "tal cual" sin garantías. No somos responsables de ningún daño que surja del uso de la aplicación.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contacto</h2>
        <p>Para preguntas sobre estos términos, contáctenos a través de la función de chat de la aplicación o envíenos un email a <a href="mailto:support@anyelo.es" className="underline underline-offset-4" style={{ color: '#C8FF00' }}>support@anyelo.es</a></p>
      </section>
    </>
  );
}

function TermsContentEN() {
  return (
    <>
      <p>
        Welcome to Anyelo Elite. By using this app, you agree to these terms.
      </p>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Service description</h2>
        <p className="mb-3">Anyelo Elite is a personal fitness coaching application that provides:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Training session booking and management</li>
          <li>Form technique videos</li>
          <li>Structured training programs</li>
          <li>Nutrition plans</li>
          <li>1-on-1 coaching chat</li>
          <li>Video consultations</li>
          <li>Progress tracking</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Eligibility</h2>
        <p>You must be at least 16 years old to use this app. By using the app, you confirm you meet this requirement.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>User accounts</h2>
        <p>You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate information during registration.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Acceptable use</h2>
        <p className="mb-3">You agree not to:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Share your account with others</li>
          <li>Use the app for any unlawful purpose</li>
          <li>Attempt to access other users' data</li>
          <li>Interfere with the app's functionality</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Payments and subscriptions</h2>
        <p>Payments for video consultation sessions are processed securely through Stripe. Monthly subscriptions renew automatically. You can cancel your subscription at any time from your account settings.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Training disclaimer</h2>
        <p>The training programs, nutrition plans, and coaching advice provided through this app are for informational purposes. Always consult a healthcare professional before starting any new exercise or nutrition program. Anyelo Elite is not responsible for any injuries that may occur during training.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Intellectual property</h2>
        <p>All content in the app, including videos, programs, and designs, is owned by Anyelo Elite and may not be reproduced without permission.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Video consultations</h2>
        <p>Video consultations are facilitated through third-party services (Jitsi Meet). We are not responsible for the availability or quality of these third-party services.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Modifications</h2>
        <p>We reserve the right to modify these terms at any time. Continued use of the app constitutes acceptance of modified terms.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Limitation of liability</h2>
        <p>Anyelo Elite is provided "as is" without warranties. We are not liable for any damages arising from use of the app.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contact</h2>
        <p>For questions about these terms, contact us through the app's chat feature or email us at <a href="mailto:support@anyelo.es" className="underline underline-offset-4" style={{ color: '#C8FF00' }}>support@anyelo.es</a></p>
      </section>
    </>
  );
}
